import { useState } from 'react';

interface UseApiReturn {
  loading: boolean;
  error: string | null;
  request: <T>(apiCall: () => Promise<Response>) => Promise<T>;
}

interface UseApiConfig {
  // This is needed to prevent the flash of the not-loading-screen
  // before the loading screen is shown. Generally True for get, and false for post
  initialLoading?: boolean;
}

export const useApi = ({ initialLoading = true }: UseApiConfig = {}): UseApiReturn => {
  const [loading, setLoading] = useState<boolean>(initialLoading);
  const [error, setError] = useState<string | null>(null);

  const request = async <T,>(
    apiCall: () => Promise<Response>
  ): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall();
      if (response.status === 204) {
        return null as unknown as T;
      }
      
      if (!response.ok) {
        // Try to parse error message from response
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // If response is not JSON, use default message
        }
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      const data: T = await response.json();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { request, loading, error };
};